import { motion } from 'framer-motion';
import { Clock, Calendar, Flame, BarChart } from 'lucide-react';
import { toast } from 'sonner';
import { resetUserProgress } from '../../lib/userSyncAuth';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_EMOJI_ELF = '🧝';
const ROLE_EMOJI_HUMAN = '⚔️';

interface ProgressTabProps {
  // Level system
  level: number;
  xp: number;
  prestigeLevel: number;
  levelPath: 'elf' | 'human';
  tempLevelSystemEnabled: boolean;
  setTempLevelSystemEnabled: (enabled: boolean) => void;
  handleRoleChange: (role: 'elf' | 'human') => void;

  // Stats
  totalPomodoros: number;
  totalStudyMinutes: number;
  totalUniqueDays: number;
  consecutiveLoginDays: number;
  firstLoginDate: string | null;

  // Boosts
  pomodoroBoostActive: boolean;
  pomodoroBoostExpiresAt: number | null;

  // Username
  usernameInput: string;
  setUsernameInput: (value: string) => void;
  usernameError: string | null;
  setUsernameError: (error: string | null) => void;
  usernameLoading: boolean;
  handleSaveUsername: () => Promise<void>;

  // Actions
  resetProgress: () => void;
}

export function ProgressTab(props: ProgressTabProps) {
  const {
    tempLevelSystemEnabled,
    setTempLevelSystemEnabled,
    levelPath,
    handleRoleChange,
    totalPomodoros,
    totalStudyMinutes,
    totalUniqueDays,
    consecutiveLoginDays,
    firstLoginDate,
    usernameInput,
    setUsernameInput,
    usernameError,
    setUsernameError,
    usernameLoading,
    handleSaveUsername,
    resetProgress,
  } = props;

  const { appUser } = useAuth();

  // Calculate study hours and minutes
  const studyHours = Math.floor(totalStudyMinutes / 60);
  const studyMins = totalStudyMinutes % 60;

  // Calculate avg session length
  const avgSessionLength = totalPomodoros > 0 ? Math.round(totalStudyMinutes / totalPomodoros) : 0;

  // Format first login date
  const formattedFirstLoginDate = firstLoginDate
    ? new Date(firstLoginDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'N/A';

  return (
    <motion.div
      key="progress"
      role="tabpanel"
      id="progress-panel"
      aria-labelledby="progress-tab"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
    >
      {/* Level System Toggle */}
      <div>
        <h3 className="text-white font-bold text-lg mb-4">Level System</h3>
        <div className="flex items-center justify-between mb-4">
          <label className="text-white">Enable leveling system</label>
          <input
            type="checkbox"
            checked={tempLevelSystemEnabled}
            onChange={(e) => setTempLevelSystemEnabled(e.target.checked)}
            className="w-5 h-5 rounded"
          />
        </div>
      </div>

      {/* Hero Role */}
      <div>
        <h3 className="text-white font-bold text-lg mb-4">Hero Role</h3>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">
              {levelPath === 'elf' ? ROLE_EMOJI_ELF : ROLE_EMOJI_HUMAN}
            </span>
            <div>
              <p className="text-white font-medium">{levelPath === 'elf' ? 'Elf' : 'Human'}</p>
              <p className="text-gray-400 text-sm">
                {levelPath === 'elf' ? 'Consistency & Focus' : 'High Risk, High Reward'}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleRoleChange(levelPath === 'elf' ? 'human' : 'elf')}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20 transition-colors text-sm"
          >
            Switch
          </button>
        </div>
      </div>

      {/* Stats */}
      <div>
        <h3 className="text-white font-bold text-lg mb-4">Your Stats</h3>

        <div className="grid grid-cols-2 gap-3 w-full max-w-full">
          {/* Pomodoros Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:border-white/20 transition-colors min-w-0">
            <div className="flex items-center gap-2 text-red-400 mb-2">
              <span className="text-base">🍅</span>
              <span className="text-xs text-gray-400 uppercase tracking-wide">Pomodoros</span>
            </div>
            <p className="text-lg font-bold text-white truncate">
              {totalPomodoros.toLocaleString()}
            </p>
          </div>

          {/* Study Time Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:border-white/20 transition-colors min-w-0">
            <div className="flex items-center gap-2 text-green-400 mb-2">
              <Clock className="w-5 h-5" />
              <span className="text-xs text-gray-400 uppercase tracking-wide">Study Time</span>
            </div>
            <p className="text-lg font-bold text-white truncate">
              {studyHours > 0 ? `${studyHours}h ${studyMins}m` : `${studyMins}m`}
            </p>
          </div>

          {/* Active Days Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:border-white/20 transition-colors min-w-0">
            <div className="flex items-center gap-2 text-cyan-400 mb-2">
              <Calendar className="w-5 h-5" />
              <span className="text-xs text-gray-400 uppercase tracking-wide">Active Days</span>
            </div>
            <p className="text-lg font-bold text-white truncate">{totalUniqueDays}</p>
          </div>

          {/* Login Streak Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:border-white/20 transition-colors min-w-0">
            <div className="flex items-center gap-2 text-orange-400 mb-2">
              <Flame className="w-5 h-5" />
              <span className="text-xs text-gray-400 uppercase tracking-wide">Login Streak</span>
            </div>
            <p className="text-lg font-bold text-white truncate">{consecutiveLoginDays} days</p>
          </div>

          {/* Avg Session Card */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:border-white/20 transition-colors min-w-0">
            <div className="flex items-center gap-2 text-purple-400 mb-2">
              <BarChart className="w-5 h-5" />
              <span className="text-xs text-gray-400 uppercase tracking-wide">Avg Session</span>
            </div>
            <p className="text-lg font-bold text-white truncate">{avgSessionLength}m</p>
          </div>

          {/* Since Card - Conditional */}
          {firstLoginDate && (
            <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10 hover:border-white/20 transition-colors min-w-0">
              <div className="flex items-center gap-2 text-pink-400 mb-2">
                <Calendar className="w-5 h-5" />
                <span className="text-xs text-gray-400 uppercase tracking-wide">Since</span>
              </div>
              <p className="text-lg font-bold text-white truncate">{formattedFirstLoginDate}</p>
            </div>
          )}
        </div>
      </div>

      {/* Username */}
      <div>
        <h3 className="text-white font-bold text-lg mb-2">Username</h3>
        <p className="text-gray-400 text-sm mb-4">
          Change your display name. Free once per week, or costs 50 XP.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={usernameInput}
            onChange={(e) => {
              setUsernameInput(e.target.value.slice(0, 20));
              setUsernameError(null);
            }}
            maxLength={20}
            disabled={usernameLoading}
            className="flex-1 min-w-0 bg-white/10 text-white px-4 py-2 rounded-lg border border-white/20 disabled:opacity-50"
            placeholder="User"
          />
          <button
            onClick={handleSaveUsername}
            disabled={usernameLoading}
            className="px-4 py-2 bg-white text-black font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {usernameLoading ? '...' : 'Save'}
          </button>
        </div>
        {usernameError && <p className="text-red-400 text-sm mt-2">⚠ {usernameError}</p>}
      </div>

      {/* Reset Progress */}
      <div>
        <h3 className="text-white font-bold text-lg mb-2">Reset Progress</h3>
        <p className="text-gray-400 text-sm mb-4">
          Reset all your progress. This cannot be undone.
        </p>
        <button
          onClick={() => {
            toast('Reset All Progress?', {
              description: 'All your XP, levels, prestige, and stats will be lost.',
              duration: 10000,
              action: {
                label: 'Reset',
                onClick: async () => {
                  try {
                    if (appUser?.id && appUser?.discord_id) {
                      await resetUserProgress(appUser.id);
                    }
                    resetProgress();
                    toast.success('Progress reset');
                  } catch (error) {
                    console.error('Failed to reset progress:', error);
                    toast.error('Failed to reset progress');
                  }
                },
              },
              cancel: {
                label: 'Cancel',
                onClick: () => {},
              },
            });
          }}
          className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
        >
          Reset All
        </button>
      </div>
    </motion.div>
  );
}
